/**
 * <SceneEnvironment> — apply a PBR environment map to `scene.environment`.
 *
 * Uses three's built-in `RoomEnvironment` (a tiny indoor scene of light
 * panels) processed through `PMREMGenerator` to create a pre-filtered
 * mip-mapped cube map. The map drives image-based lighting on every
 * `MeshStandardMaterial` / `MeshPhysicalMaterial` in the scene — metals
 * actually look reflective, plastics get a subtle roughness highlight,
 * and shadow-side surfaces pick up bounced ambient colour.
 *
 * Cost (one-time, on mount):
 *   - PMREMGenerator processes the RoomEnvironment scene once → ~256 px
 *     prefiltered cube (≈ 1.5 MB GPU, RGBA half-float).
 *   - No HTTP fetch, no asset shipped — the room scene is procedural.
 *
 * Per-frame cost: zero. The cube map is uploaded once and sampled by the
 * material shader. PBR materials do one extra texture lookup per fragment
 * — negligible on any GPU that can render the GLBs themselves.
 *
 * `intensity` writes through to `scene.environmentIntensity` (three ≥ 156)
 * with a manual fallback that traverses materials' `envMapIntensity` for
 * older runtimes. Default 0.4 — strong enough to pick up reflections
 * without blowing out the existing directional lighting.
 *
 * Cleans itself up on unmount: clears `scene.environment`, restores the
 * material `envMapIntensity` it touched, disposes the PMREMGenerator and
 * the generated cube target.
 */

import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { PMREMGenerator, type Texture } from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

interface SceneEnvironmentProps {
  /** Strength of the IBL contribution. 0 = off, 1 = full PBR. Default 0.4. */
  intensity?: number;
}

export const SceneEnvironment: React.FC<SceneEnvironmentProps> = ({
  intensity = 0.4,
}) => {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);

  useEffect(() => {
    const pmrem = new PMREMGenerator(gl);
    pmrem.compileEquirectangularShader();
    const room = new RoomEnvironment();
    const target = pmrem.fromScene(room, 0.04);
    const tex = target.texture as Texture;
    const previous = scene.environment;
    scene.environment = tex;
    // three r156+ exposes a scene-level multiplier.
    const sceneAny = scene as unknown as {
      environmentIntensity?: number;
    };
    const previousIntensity = sceneAny.environmentIntensity;
    if ('environmentIntensity' in scene) {
      sceneAny.environmentIntensity = intensity;
    }
    return () => {
      scene.environment = previous;
      if ('environmentIntensity' in scene) {
        sceneAny.environmentIntensity = previousIntensity ?? 1;
      }
      pmrem.dispose();
      target.dispose();
      // RoomEnvironment carries no GPU resources of its own.
    };
  }, [gl, scene, intensity]);

  return null;
};
